import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowUp,
  ArrowDown,
  CalendarPlus,
  Crop as CropIcon,
  Clock,
  Image as ImageIcon,
  PaperPlaneRight,
  PencilSimple,
  TextAa,
  TextHOne,
  TextHTwo,
  TextT,
  Trash,
  UsersThree,
  X,
} from '@phosphor-icons/react';

import {
  createBroadcast,
  getBroadcast,
  previewAudience,
  sendBroadcast,
  setBroadcastWebsite,
  updateBroadcast,
  uploadBroadcastImage,
  type Broadcast,
  type BroadcastAudience,
  type BroadcastBlock,
  type BroadcastBlockType,
  type BroadcastSavePayload,
  type BroadcastTranslation,
} from '../api/broadcasts';
import { getOrgGeneral, listMyOrganizations, type OrgGeneralInfo } from '../api/orgs';
import { ApiError } from '../api/client';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { useConfirm } from '../components/ConfirmDialog';
import { ImageCropDialog } from '../components/ImageCropDialog';
import { PLATFORM_LANGUAGES } from '../components/LanguagesField';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePickerPopover } from '@/components/ui/date-picker-popover';
import { TimePickerPopover } from '@/components/ui/time-picker-popover';
import { RecipientPicker } from '../broadcasts/RecipientPicker';

// BroadcastEditor at /orgs/:slug/broadcasts/:id (or /new). Composes a
// new draft or edits an existing one, then sends. Sent broadcasts open
// here too in read-only mode (the editor disables every input when the
// row's status is "sent").
//
// Layout: General + Recipients (compact summary) stacked at top, then a
// side-by-side Content area with the block builder on the left and a
// live email preview on the right (mirrors the club-branded chrome the
// real email gets wrapped in by internal/email/templates/layout).

interface DraftState {
  subject: string;
  blocks: BroadcastBlock[];
  audience: BroadcastAudience;
  publish_to_website: boolean;
  include_header: boolean;
  include_footer: boolean;
  source_language: string;
  translations: Record<string, BroadcastTranslation>;
  // scheduled_at is an RFC3339 timestamp when this draft is scheduled to
  // send automatically, or null for a plain draft (sent by hand).
  scheduled_at: string | null;
}

const emptyDraft: DraftState = {
  subject: '',
  blocks: [],
  audience: {},
  publish_to_website: false,
  include_header: true,
  include_footer: true,
  // Empty = "not chosen yet" → resolved to the org's own language by
  // sourceLang below. Never hardcode a language here: source_language decides
  // which site languages the article renders in, so a wrong guess hides a
  // club's news from its own website.
  source_language: '',
  translations: {},
  scheduled_at: null,
};

function fromBroadcast(b: Broadcast): DraftState {
  return {
    subject: b.subject,
    blocks: b.blocks,
    audience: b.audience,
    publish_to_website: b.publish_to_website,
    include_header: b.include_header,
    include_footer: b.include_footer,
    source_language: b.source_language,
    translations: b.translations ?? {},
    scheduled_at: b.scheduled_at ?? null,
  };
}

export function BroadcastEditorPage() {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { slug = '', id: routeID = '' } = useParams<{ slug: string; id: string }>();
  const confirm = useConfirm();
  const token = auth.user?.access_token ?? null;
  const isNew = routeID === 'new' || routeID === '';

  // A draft handed over from the "Create with AI" dialog on the list page.
  // Carried via navigation state so nothing is persisted until the admin
  // saves or sends. Only honored on the /new route.
  const aiDraft = (location.state as { aiDraft?: Pick<DraftState, 'subject' | 'blocks'> } | null)
    ?.aiDraft;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [broadcast, setBroadcast] = useState<Broadcast | null>(null);
  const [draft, setDraft] = useState<DraftState>(() =>
    isNew && aiDraft
      ? { ...emptyDraft, subject: aiDraft.subject, blocks: aiDraft.blocks }
      : emptyDraft,
  );
  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const [orgInfo, setOrgInfo] = useState<OrgGeneralInfo | null>(null);
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const [websiteSaving, setWebsiteSaving] = useState(false);

  // The org's own language — cached by the shell under ['my-orgs'] (GET
  // /api/v1/me/organizations carries language_code), so this reuses the cache
  // rather than fetching. It's the fallback source language for a broadcast
  // that hasn't recorded one, mirroring the server-side fallback in
  // broadcasts.resolveSourceLang so the SPA and the API never disagree.
  const orgsQuery = useQuery({
    queryKey: ['my-orgs'],
    queryFn: () => listMyOrganizations(token),
    enabled: !!token,
  });
  const orgLang = orgsQuery.data?.find((o) => o.slug === slug)?.language_code;
  const sourceLang = draft.source_language || orgLang || PLATFORM_LANGUAGES[0]!.code;

  // Load on mount when editing an existing broadcast.
  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    void (async () => {
      try {
        const b = await getBroadcast(token, slug, routeID);
        if (!cancelled) {
          setBroadcast(b);
          setDraft(fromBroadcast(b));
        }
      } catch (err) {
        if (!cancelled) {
          setErrorCode(err instanceof ApiError ? err.code : 'unknown');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNew, routeID, slug, token]);

  // Load the org branding once so the preview can render the same logo /
  // primary color / org name the real email will use.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const info = await getOrgGeneral(token, slug);
        if (!cancelled) setOrgInfo(info);
      } catch {
        // Branding is decorative; failure here doesn't block editing.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, token]);

  // Live recipient-count preview as the audience picker changes.
  useEffect(() => {
    let cancelled = false;
    const empty =
      !draft.audience.all_members &&
      (draft.audience.team_ids?.length ?? 0) === 0 &&
      (draft.audience.member_ids?.length ?? 0) === 0;
    if (empty) {
      setRecipientCount(0);
      return;
    }
    const handle = setTimeout(() => {
      void (async () => {
        try {
          const p = await previewAudience(token, slug, draft.audience);
          if (!cancelled) setRecipientCount(p.email_count);
        } catch {
          if (!cancelled) setRecipientCount(null);
        }
      })();
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [draft.audience, slug, token]);

  const readOnly = broadcast?.status === 'sent';

  // Publish-to-website is the one field that stays editable after send.
  // Toggling only updates local state; on a sent broadcast the change is
  // persisted by the dedicated Save button (websiteDirty/onSaveWebsite
  // below), and on a draft it rides along with the normal Save / Send.
  const websiteDirty =
    readOnly && broadcast != null && draft.publish_to_website !== broadcast.publish_to_website;

  const onSaveWebsite = useCallback(async () => {
    if (!broadcast) return;
    setWebsiteSaving(true);
    setErrorCode(null);
    try {
      const updated = await setBroadcastWebsite(token, slug, broadcast.id, draft.publish_to_website);
      setBroadcast(updated);
      setDraft((d) => ({ ...d, publish_to_website: updated.publish_to_website }));
    } catch (err) {
      setErrorCode(err instanceof ApiError ? err.code : 'unknown');
    } finally {
      setWebsiteSaving(false);
    }
  }, [broadcast, draft.publish_to_website, slug, token]);

  const persistDraft = useCallback(async (
    // scheduledOverride replaces the draft's own scheduled_at for this save:
    // pass an ISO string to (re)schedule, or null to explicitly clear the
    // schedule (used by "send now"). Omit to persist the current draft value.
    scheduledOverride?: string | null,
  ): Promise<Broadcast | null> => {
    const scheduledAt =
      scheduledOverride !== undefined ? scheduledOverride : draft.scheduled_at;
    const payload: BroadcastSavePayload = {
      subject: draft.subject.trim(),
      blocks: draft.blocks,
      audience: draft.audience,
      publish_to_website: draft.publish_to_website,
      include_header: draft.include_header,
      include_footer: draft.include_footer,
      // Send the resolved language, not the raw draft field: an untouched draft
      // carries "" and the article's language must be recorded explicitly.
      source_language: sourceLang,
      // Only carry translations when the broadcast is actually going to the
      // website; an email-only broadcast doesn't need them.
      translations: draft.publish_to_website ? draft.translations : {},
      scheduled_at: scheduledAt,
    };
    if (!payload.subject) {
      setErrorCode('subject_required');
      return null;
    }
    if (payload.blocks.length === 0) {
      setErrorCode('blocks_required');
      return null;
    }
    setSaving(true);
    setErrorCode(null);
    try {
      const saved = broadcast
        ? await updateBroadcast(token, slug, broadcast.id, payload)
        : await createBroadcast(token, slug, payload);
      setBroadcast(saved);
      setDraft(fromBroadcast(saved));
      return saved;
    } catch (err) {
      setErrorCode(err instanceof ApiError ? err.code : 'unknown');
      return null;
    } finally {
      setSaving(false);
    }
  }, [broadcast, draft, slug, token, sourceLang]);

  const onSaveDraft = useCallback(async () => {
    const saved = await persistDraft();
    if (saved) {
      navigate(`/orgs/${slug}/broadcasts/${saved.id}`, { replace: true });
    }
  }, [navigate, persistDraft, slug]);

  const onSend = useCallback(async () => {
    // Sending now supersedes any schedule — persist with the schedule cleared
    // so the row goes out immediately rather than also being worker-eligible.
    const saved = await persistDraft(null);
    if (!saved) return;
    const audienceLabel = recipientCount ?? 0;
    if (
      !(await confirm({
        title: t('broadcast.editor.sendConfirmTitle', { defaultValue: 'Send this utskick?' }),
        body: t('broadcast.editor.sendConfirmBody', {
          count: audienceLabel,
          defaultValue: `This will email ${audienceLabel} recipients and cannot be undone.`,
        }),
        confirmLabel: t('broadcast.editor.send', { defaultValue: 'Send now' }),
        tone: 'destructive',
      }))
    )
      return;
    setSending(true);
    setErrorCode(null);
    try {
      await sendBroadcast(token, slug, saved.id);
      // Flag the send so the list re-polls — the status flips draft → sent
      // only once the async fan-out completes server-side.
      navigate(`/orgs/${slug}/broadcasts`, { state: { sent: true } });
    } catch (err) {
      setErrorCode(err instanceof ApiError ? err.code : 'unknown');
    } finally {
      setSending(false);
    }
  }, [confirm, navigate, persistDraft, recipientCount, slug, t, token]);

  const [scheduleOpen, setScheduleOpen] = useState(false);

  // onSchedule persists the draft with a future scheduled_at, so the worker
  // sends it automatically at that time. Reuses the same validation as a
  // manual send (non-empty audience or website publish), plus a future-time
  // check the server also enforces.
  const onSchedule = useCallback(
    async (iso: string) => {
      const saved = await persistDraft(iso);
      if (!saved) return;
      setScheduleOpen(false);
      navigate(`/orgs/${slug}/broadcasts`, { state: { scheduled: true } });
    },
    [navigate, persistDraft, slug],
  );

  // onCancelSchedule clears the schedule, leaving a plain draft in place. Stays
  // on the editor so the admin can keep editing or send by hand.
  const onCancelSchedule = useCallback(async () => {
    if (
      !(await confirm({
        title: t('broadcast.editor.cancelScheduleConfirmTitle', {
          defaultValue: 'Cancel scheduled send?',
        }),
        body: t('broadcast.editor.cancelScheduleConfirmBody', {
          defaultValue: 'The broadcast stays a draft. You can send or reschedule it later.',
        }),
        confirmLabel: t('broadcast.editor.cancelScheduleCta', { defaultValue: 'Cancel schedule' }),
        tone: 'destructive',
      }))
    )
      return;
    await persistDraft(null);
  }, [confirm, persistDraft, t]);

  // Block helpers
  const addBlock = useCallback((type: BroadcastBlockType) => {
    setDraft((d) => ({
      ...d,
      blocks: [...d.blocks, type === 'image' ? { type, object_key: '' } : { type, text: '' }],
    }));
  }, []);

  const updateBlock = useCallback((idx: number, patch: Partial<BroadcastBlock>) => {
    setDraft((d) => ({
      ...d,
      blocks: d.blocks.map((b, i) => (i === idx ? { ...b, ...patch } : b)),
    }));
  }, []);

  const removeBlock = useCallback((idx: number) => {
    setDraft((d) => ({
      ...d,
      blocks: d.blocks.filter((_, i) => i !== idx),
    }));
  }, []);

  const moveBlock = useCallback((idx: number, dir: -1 | 1) => {
    setDraft((d) => {
      const next = [...d.blocks];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return d;
      [next[idx], next[target]] = [next[target] as BroadcastBlock, next[idx] as BroadcastBlock];
      return { ...d, blocks: next };
    });
  }, []);

  const uploadBroadcastFile = useCallback(
    (file: File) => uploadBroadcastImage(token, slug, file),
    [slug, token],
  );

  // Locale-aware label for a scheduled_at timestamp: medium date + 24h time
  // (the product-wide clock convention). Used by the banner + schedule dialog.
  const formatScheduledLabel = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const date = new Intl.DateTimeFormat(i18n.language || undefined, {
      dateStyle: 'medium',
    }).format(d);
    const time = new Intl.DateTimeFormat(i18n.language || undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
    return `${date} ${time}`;
  };

  if (loading) {
    return <LoadingOverlay open />;
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 p-4 md:p-8">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {broadcast?.status === 'sent'
              ? t('broadcast.editor.eyebrowSent', { defaultValue: 'Sent utskick' })
              : t('broadcast.editor.eyebrowDraft', { defaultValue: 'New utskick' })}
          </p>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight md:text-3xl">
            {draft.subject || t('broadcast.editor.untitled', { defaultValue: 'Untitled' })}
          </h1>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/orgs/${slug}/broadcasts`)}
          >
            <X className="h-4 w-4" />
            {t('common.cancel')}
          </Button>
          {!readOnly && (
            <>
              <Button type="button" variant="outline" onClick={onSaveDraft} disabled={saving || sending}>
                {t('broadcast.editor.saveDraft', { defaultValue: 'Save draft' })}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setScheduleOpen(true)}
                disabled={saving || sending}
              >
                <CalendarPlus className="h-4 w-4" weight="bold" />
                {draft.scheduled_at
                  ? t('broadcast.editor.reschedule', { defaultValue: 'Reschedule' })
                  : t('broadcast.editor.schedule', { defaultValue: 'Schedule' })}
              </Button>
              <Button type="button" onClick={onSend} disabled={saving || sending}>
                <PaperPlaneRight className="h-4 w-4" weight="bold" />
                {sending
                  ? t('broadcast.editor.sending', { defaultValue: 'Sending…' })
                  : t('broadcast.editor.send', { defaultValue: 'Send now' })}
              </Button>
            </>
          )}
          {readOnly && (
            <Button
              type="button"
              onClick={() => void onSaveWebsite()}
              disabled={!websiteDirty || websiteSaving}
            >
              {websiteSaving
                ? t('common.submitting', { defaultValue: 'Saving…' })
                : t('broadcast.editor.saveWebsite', { defaultValue: 'Save changes' })}
            </Button>
          )}
        </div>
      </header>

      {errorCode && (
        <p
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-sm text-destructive"
        >
          {t(`broadcast.editor.error.${errorCode}`, {
            defaultValue: t('broadcast.editor.error.unknown', {
              defaultValue: 'Something went wrong. Please try again.',
            }),
          })}
        </p>
      )}

      {!readOnly && draft.scheduled_at && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-primary/5 p-3">
          <span className="flex items-center gap-2 text-sm font-medium">
            <Clock className="h-4 w-4 text-muted-foreground" weight="bold" aria-hidden />
            {t('broadcast.editor.scheduledBanner', {
              when: formatScheduledLabel(draft.scheduled_at),
              defaultValue: `Scheduled to send ${formatScheduledLabel(draft.scheduled_at)}`,
            })}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void onCancelSchedule()}
            disabled={saving || sending}
          >
            {t('broadcast.editor.cancelScheduleCta', { defaultValue: 'Cancel schedule' })}
          </Button>
        </div>
      )}

      <section className="rounded-2xl border border-border bg-card p-5">
        <h2 className="mb-3 font-heading text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          {t('broadcast.editor.generalHeading', { defaultValue: 'General' })}
        </h2>
        <div className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              {t('broadcast.editor.subjectLabel', { defaultValue: 'Subject' })}
            </span>
            <Input
              type="text"
              value={draft.subject}
              onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
              maxLength={200}
              disabled={readOnly}
              placeholder={t('broadcast.editor.subjectPlaceholder', { defaultValue: 'Subject' })}
            />
          </label>

          {/* Recipients summary — the actual picker lives in a dialog so
              the main page stays focused on composing. */}
          <div className="flex flex-col gap-2 rounded-xl border border-border bg-background p-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <UsersThree className="h-5 w-5 text-muted-foreground" weight="bold" />
                <div className="flex flex-col">
                  <span className="font-heading text-sm font-semibold">
                    {t('broadcast.editor.recipientsHeading', { defaultValue: 'Recipients' })}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {buildRecipientSummary(draft.audience, recipientCount).map((key, idx) =>
                      idx === 0 ? (
                        <span key={key.id}>{t(key.id, key.opts as Record<string, unknown>)}</span>
                      ) : (
                        <span key={key.id}>
                          {' · '}
                          {t(key.id, key.opts as Record<string, unknown>)}
                        </span>
                      ),
                    )}
                  </span>
                </div>
              </div>
              {!readOnly && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setRecipientsOpen(true)}
                >
                  <PencilSimple className="h-4 w-4" weight="bold" />
                  {t('broadcast.editor.editRecipients', { defaultValue: 'Edit recipients' })}
                </Button>
              )}
            </div>
          </div>

          {/* Publish-to-website + header/footer toggles. Grouped so the
              author can see the broadcast's destinations + chrome state
              at a glance. */}
          <div className="flex flex-col gap-2.5">
            <label className="flex items-start gap-3">
              <Checkbox
                checked={draft.publish_to_website}
                onChange={(e) => setDraft((d) => ({ ...d, publish_to_website: e.target.checked }))}
                disabled={websiteSaving}
                className="mt-0.5"
              />
              <span className="flex flex-col gap-0.5">
                <span className="font-heading text-sm font-semibold">
                  {t('broadcast.editor.publishToWebsiteLabel', {
                    defaultValue: 'Show as news on public website',
                  })}
                </span>
                <span className="text-xs text-muted-foreground">
                  {readOnly
                    ? t('broadcast.editor.publishToWebsiteHintSent', {
                        defaultValue:
                          'This broadcast has already been sent. You can still show or hide it as a news article on your public site.',
                      })
                    : t('broadcast.editor.publishToWebsiteHint', {
                        defaultValue:
                          'When enabled, this utskick appears as a news article on your public site after sending.',
                      })}
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3">
              <Checkbox
                checked={draft.include_header}
                onChange={(e) => setDraft((d) => ({ ...d, include_header: e.target.checked }))}
                disabled={readOnly}
                className="mt-0.5"
              />
              <span className="flex flex-col gap-0.5">
                <span className="font-heading text-sm font-semibold">
                  {t('broadcast.editor.includeHeaderLabel', { defaultValue: 'Include header' })}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t('broadcast.editor.includeHeaderHint', {
                    defaultValue:
                      'Shows your club logo and name at the top of the email.',
                  })}
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3">
              <Checkbox
                checked={draft.include_footer}
                onChange={(e) => setDraft((d) => ({ ...d, include_footer: e.target.checked }))}
                disabled={readOnly}
                className="mt-0.5"
              />
              <span className="flex flex-col gap-0.5">
                <span className="font-heading text-sm font-semibold">
                  {t('broadcast.editor.includeFooterLabel', { defaultValue: 'Include footer' })}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t('broadcast.editor.includeFooterHint', {
                    defaultValue:
                      'Shows the club attribution and contact info at the bottom of the email.',
                  })}
                </span>
              </span>
            </label>
          </div>

          {/* Per-broadcast translations are intentionally not exposed for now.
              The authoring UI lives in ../broadcasts/TranslationsSection and the
              storage (source_language + translations) is untouched, so this is a
              presentation-level hide, not a removal — re-render the section here
              to bring it back. Deferred because translations currently only
              reach the public website article, not the email or the in-app
              inbox, which is where a parent who doesn't read the club's language
              would actually see the message. Not worth the authoring step until
              the send path localizes per recipient.

              source_language is still recorded on every save (see sourceLang
              above, derived from the org's language), so website articles keep
              resolving in the org's language lane. The only thing lost while
              hidden: an author writing in a language other than the org default
              can't say so, and the article files under the org language. */}
        </div>
      </section>

      {/* Content area: builder on the LEFT, live preview on the RIGHT.
          On screens narrower than `lg` the preview drops below the
          builder so neither side gets squeezed. */}
      <section className="rounded-2xl border border-border bg-card p-5">
        <h2 className="mb-3 font-heading text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          {t('broadcast.editor.contentHeading', { defaultValue: 'Content' })}
        </h2>

        <div className="grid gap-5 lg:grid-cols-2">
          <div className="flex flex-col gap-3">
            <ul className="flex flex-col gap-2.5">
              {draft.blocks.map((block, idx) => (
                <li key={idx} className="rounded-xl border border-border bg-background p-3">
                  <BlockEditor
                    block={block}
                    disabled={readOnly}
                    onChange={(patch) => updateBlock(idx, patch)}
                    onRemove={() => removeBlock(idx)}
                    onMoveUp={idx > 0 ? () => moveBlock(idx, -1) : undefined}
                    onMoveDown={
                      idx < draft.blocks.length - 1 ? () => moveBlock(idx, 1) : undefined
                    }
                    upload={uploadBroadcastFile}
                  />
                </li>
              ))}
            </ul>
            {!readOnly && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {t('broadcast.editor.addBlock', { defaultValue: 'Add block:' })}
                </span>
                <Button type="button" variant="outline" size="sm" onClick={() => addBlock('title')}>
                  <TextHOne className="h-4 w-4" />
                  {t('broadcast.editor.blockTitle', { defaultValue: 'Title' })}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => addBlock('subtitle')}>
                  <TextHTwo className="h-4 w-4" />
                  {t('broadcast.editor.blockSubtitle', { defaultValue: 'Subtitle' })}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => addBlock('body')}>
                  <TextAa className="h-4 w-4" />
                  {t('broadcast.editor.blockBody', { defaultValue: 'Body' })}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => addBlock('image')}>
                  <ImageIcon className="h-4 w-4" />
                  {t('broadcast.editor.blockImage', { defaultValue: 'Image' })}
                </Button>
              </div>
            )}
          </div>

          <div>
            <p className="mb-2 font-heading text-[0.7rem] font-semibold uppercase tracking-wider text-muted-foreground">
              {t('broadcast.editor.previewLabel', { defaultValue: 'Preview' })}
            </p>
            <BroadcastPreview
              subject={draft.subject}
              blocks={draft.blocks}
              includeHeader={draft.include_header}
              includeFooter={draft.include_footer}
              orgInfo={orgInfo}
              emptyLabel={t('broadcast.editor.previewEmpty', {
                defaultValue: 'Add a block to see a preview.',
              })}
            />
          </div>
        </div>
      </section>

      <RecipientsDialog
        open={recipientsOpen}
        onClose={() => setRecipientsOpen(false)}
        slug={slug}
        token={token}
        value={draft.audience}
        onChange={(audience) => setDraft((d) => ({ ...d, audience }))}
        disabled={readOnly}
        recipientCount={recipientCount}
      />

      <ScheduleDialog
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        initialISO={draft.scheduled_at}
        recipientCount={recipientCount}
        saving={saving}
        onConfirm={onSchedule}
      />
    </div>
  );
}

// ScheduleDialog collects a future date + 24h time and returns the combined
// RFC3339 instant. Uses DatePickerPopover + TimePickerPopover — click-to-pick
// beats typing here, because "schedule this for later" is a browsing decision
// (which weekday is the 14th? is that during the school break?) rather than a
// transcription one. Both stay locale-independent (ISO date, 24h clock) per
// the product convention. The combined local wall-clock is converted to a UTC
// ISO string on confirm; the server re-validates that it's in the future.
//
// The <dialog> carries `overflow-visible` and the calendar portals into the
// dialog node: showModal() puts the dialog in the top layer, so a panel
// portaled to document.body would render behind it, and the UA stylesheet's
// `overflow: auto` on <dialog> would otherwise clip a panel that hangs below
// the field.
function ScheduleDialog({
  open,
  onClose,
  initialISO,
  recipientCount,
  saving,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  initialISO: string | null;
  recipientCount: number | null;
  saving: boolean;
  onConfirm: (iso: string) => void | Promise<void>;
}) {
  const { t, i18n } = useTranslation('common');
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  // Held in state, not just the ref: the calendar popover needs the portal
  // host on the render that mounts it, and a ref assignment doesn't re-render.
  const [portalHost, setPortalHost] = useState<HTMLDialogElement | null>(null);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  // Seed the fields from an existing schedule (or a sensible default: the next
  // whole hour) each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setLocalError(null);
    const seed = initialISO ? new Date(initialISO) : defaultScheduleSeed();
    if (Number.isNaN(seed.getTime())) {
      setDate('');
      setTime('');
      return;
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    setDate(`${seed.getFullYear()}-${pad(seed.getMonth() + 1)}-${pad(seed.getDate())}`);
    setTime(`${pad(seed.getHours())}:${pad(seed.getMinutes())}`);
  }, [open, initialISO]);

  const submit = () => {
    const iso = combineToISO(date, time);
    if (!iso) {
      setLocalError('invalid');
      return;
    }
    if (new Date(iso).getTime() <= Date.now()) {
      setLocalError('past');
      return;
    }
    void onConfirm(iso);
  };

  return (
    <dialog
      ref={(node) => {
        dialogRef.current = node;
        setPortalHost(node);
      }}
      className="overflow-visible rounded-2xl border border-border bg-background p-0 shadow-2xl backdrop:bg-foreground/40"
      onClose={onClose}
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose();
      }}
    >
      <div className="flex w-[min(28rem,90vw)] flex-col gap-4 p-6 text-foreground">
        <header className="flex items-start justify-between gap-3">
          <h2 className="font-heading text-xl font-bold">
            {t('broadcast.editor.scheduleDialogTitle', { defaultValue: 'Schedule send' })}
          </h2>
          <button
            type="button"
            aria-label={t('common.close', { defaultValue: 'Close' })}
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-pill text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </header>

        <p className="text-sm text-muted-foreground">
          {recipientCount !== null && recipientCount > 0
            ? t('broadcast.editor.scheduleDialogBody', {
                count: recipientCount,
                defaultValue: `The broadcast will send automatically to ${recipientCount} recipients at the time you pick.`,
              })
            : t('broadcast.editor.scheduleDialogBodyNoCount', {
                defaultValue: 'The broadcast will send automatically at the time you pick.',
              })}
        </p>

        {/* Stacks on a narrow phone so both pickers keep a full-width tap
            target; side by side from 480px up. */}
        <div className="grid grid-cols-1 items-start gap-3 min-[480px]:grid-cols-[1.4fr_1fr]">
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              {t('broadcast.editor.scheduleDate', { defaultValue: 'Date' })}
            </span>
            <DatePickerPopover
              value={date}
              onChange={setDate}
              locale={i18n.language}
              min={todayISO()}
              placeholder={t('scheduleDialog.datePlaceholder', { defaultValue: 'Pick a date' })}
              container={portalHost}
            />
          </div>
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              {t('broadcast.editor.scheduleTime', { defaultValue: 'Time' })}
            </span>
            <TimePickerPopover
              value={time}
              onChange={setTime}
              minuteStep={5}
              ariaLabel={t('broadcast.editor.scheduleTime', { defaultValue: 'Time' })}
            />
          </div>
        </div>

        {localError && (
          <p role="alert" className="text-sm text-destructive">
            {t(`broadcast.editor.scheduleError.${localError}`, {
              defaultValue:
                localError === 'past'
                  ? 'Pick a time in the future.'
                  : 'Enter a valid date and time.',
            })}
          </p>
        )}

        <footer className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button type="button" onClick={submit} disabled={saving}>
            <CalendarPlus className="h-4 w-4" weight="bold" />
            {t('broadcast.editor.scheduleConfirm', { defaultValue: 'Schedule' })}
          </Button>
        </footer>
      </div>
    </dialog>
  );
}

// defaultScheduleSeed returns the next whole hour from now — a friendlier
// default than "right now" for a scheduled send.
function defaultScheduleSeed(): Date {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return d;
}

// todayISO is the calendar floor for the schedule date picker — a send can't
// be scheduled into the past. It only greys out whole days that are already
// over; the exact "must be in the future" check still runs on submit.
function todayISO(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// combineToISO turns an ISO date (YYYY-MM-DD) + 24h time (HH:MM) into a UTC
// RFC3339 instant, interpreting the inputs as local wall-clock. Returns null
// when either field is malformed.
function combineToISO(date: string, time: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) {
    return null;
  }
  const d = new Date(`${date}T${time}:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

// buildRecipientSummary returns the i18n keys + count opts the compact
// recipients summary should render. Returning structured data instead of
// pre-translated strings keeps the helper untyped to react-i18next's
// TFunction and lets the caller plurals-resolve via t().
function buildRecipientSummary(
  audience: BroadcastAudience,
  count: number | null,
): Array<{ id: string; opts: Record<string, unknown> }> {
  if (audience.all_members) {
    return count !== null
      ? [
          {
            id: 'broadcast.editor.recipientSummary.allWithCount',
            opts: { count, defaultValue: `All members (${count})` },
          },
        ]
      : [
          {
            id: 'broadcast.editor.recipientSummary.all',
            opts: { defaultValue: 'All members' },
          },
        ];
  }
  const teamCount = audience.team_ids?.length ?? 0;
  const memberCount = audience.member_ids?.length ?? 0;
  if (teamCount === 0 && memberCount === 0) {
    return [
      {
        id: 'broadcast.editor.recipientSummary.none',
        opts: { defaultValue: 'No recipients yet' },
      },
    ];
  }
  if (count !== null) {
    return [
      {
        id: 'broadcast.editor.recipientSummary.count',
        opts: { count, defaultValue: `${count} recipients` },
      },
    ];
  }
  // Count preview hasn't resolved yet — show the structural fallback.
  const parts: Array<{ id: string; opts: Record<string, unknown> }> = [];
  if (teamCount > 0) {
    parts.push({
      id: 'broadcast.editor.recipientSummary.teams',
      opts: { count: teamCount, defaultValue: `${teamCount} teams` },
    });
  }
  if (memberCount > 0) {
    parts.push({
      id: 'broadcast.editor.recipientSummary.individuals',
      opts: { count: memberCount, defaultValue: `${memberCount} individuals` },
    });
  }
  return parts;
}

// RecipientsDialog wraps the existing RecipientPicker in a <dialog>
// modal. Same pattern as TeamModal — backdrop click + Esc close, focus
// trapped by the native dialog element.
function RecipientsDialog({
  open,
  onClose,
  slug,
  token,
  value,
  onChange,
  disabled,
  recipientCount,
}: {
  open: boolean;
  onClose: () => void;
  slug: string;
  token: string | null;
  value: BroadcastAudience;
  onChange: (next: BroadcastAudience) => void;
  disabled: boolean;
  recipientCount: number | null;
}) {
  const { t } = useTranslation('common');
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      className="rounded-2xl border border-border bg-background p-0 shadow-2xl backdrop:bg-foreground/40"
      onClose={onClose}
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose();
      }}
    >
      <div className="flex w-[min(40rem,90vw)] flex-col gap-4 p-6 text-foreground">
        <header className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-xl font-bold">
              {t('broadcast.editor.recipientsDialogTitle', { defaultValue: 'Edit recipients' })}
            </h2>
            {recipientCount !== null && (
              <p className="text-xs text-muted-foreground">
                {t('broadcast.editor.recipientCount', {
                  count: recipientCount,
                  defaultValue: `${recipientCount} recipients`,
                })}
              </p>
            )}
          </div>
          <button
            type="button"
            aria-label={t('common.close', { defaultValue: 'Close' })}
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-pill text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </header>

        <RecipientPicker
          slug={slug}
          token={token}
          value={value}
          onChange={onChange}
          disabled={disabled}
        />

        <footer className="flex justify-end">
          <Button type="button" onClick={onClose}>
            {t('broadcast.editor.recipientsDialogDone', { defaultValue: 'Done' })}
          </Button>
        </footer>
      </div>
    </dialog>
  );
}

// BroadcastPreview renders the broadcast inside a visual mirror of the
// blockImageSrc resolves an image block to a renderable URL: the /storage
// proxy for an uploaded object_key, else the absolute image_url (a baked-in
// stock photo on a seed-imported broadcast), else empty. Mirrors the
// backend Block.ImageSrc precedence so the preview matches the email.
function blockImageSrc(b: BroadcastBlock): string {
  if (b.object_key) return `/storage/${b.object_key}`;
  if (b.image_url) return b.image_url;
  return '';
}

// imageWidthPct mirrors backend Block.ImageWidthPct: the stored width clamped
// to [10,100], defaulting to 100 when unset, so the live preview matches the
// rendered email.
function imageWidthPct(b: BroadcastBlock): number {
  const w = b.width ?? 0;
  if (w <= 0) return 100;
  return Math.min(100, Math.max(10, w));
}

// normalizeBrandHex returns a "#rrggbb" string for the brand colour stored on
// the org, or null if the value is missing / not a 6-digit hex. The org's
// primary_color column allows the leading '#' to be optional in some seeds.
function normalizeBrandHex(raw: string | undefined | null): string | null {
  if (!raw) return null;
  const v = raw.trim().replace(/^#/, '');
  return /^[0-9a-fA-F]{6}$/.test(v) ? `#${v}` : null;
}

// BroadcastPreview renders the broadcast in the same "club letterhead"
// shape as the outgoing email (internal/email/templates/layout) and the
// mobile in-app detail screen (app/app/news/[id].tsx): a 4px brand-coloured
// top stripe, a sender row reading like the From line of an email (logo +
// org name + sent date), the subject as a 24px headline in the body, then
// the block stream, then a single centred attribution line. The three
// surfaces converge so an admin's preview matches both the recipient's
// inbox and the recipient's phone view of the same broadcast.
function BroadcastPreview({
  subject,
  blocks,
  includeHeader,
  includeFooter,
  orgInfo,
  emptyLabel,
}: {
  subject: string;
  blocks: BroadcastBlock[];
  includeHeader: boolean;
  includeFooter: boolean;
  orgInfo: OrgGeneralInfo | null;
  emptyLabel: string;
}) {
  const { t, i18n } = useTranslation('common');
  const visible = blocks.filter((b) =>
    b.type === 'image' ? !!blockImageSrc(b) : !!(b.text && b.text.trim()),
  );

  // Logo URL: the same path the SPA uses elsewhere for org logos. Lets
  // the editor render the same image the email will deliver.
  const logoSrc = orgInfo?.logo_object_key ? `/storage/${orgInfo.logo_object_key}` : null;
  const orgName = orgInfo?.name ?? t('broadcast.editor.previewOrgFallback', { defaultValue: 'SportAdmin Go' });
  // Hypothetical sent date for the preview: today, formatted in the admin's
  // current SPA language. The real send-time renderer formats the same way
  // in the org's primary language, so the preview is representative of what
  // a recipient in that locale would see.
  const sentAtLabel = useMemo(() => {
    return new Intl.DateTimeFormat(i18n.language || 'en', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date());
  }, [i18n.language]);
  // Brand stripe colour — the org's primary when set, lime as the platform
  // default. Same fallback the backend layout package applies.
  const brandHex = normalizeBrandHex(orgInfo?.primary_color) ?? '#BEE034';

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-[#F5F5F0] px-4 py-8">
      <div className="mx-auto max-w-[600px] overflow-hidden rounded-[24px] border border-[#EAEAEA] bg-white">
        {includeHeader && (
          <>
            <div style={{ height: 4, background: brandHex }} aria-hidden />
            <div className="flex items-center gap-3 border-b border-[#EAEAEA] px-8 py-5">
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt={orgName}
                  className="block h-10 w-auto max-w-[140px] object-contain"
                />
              ) : (
                <span className="font-heading text-lg font-extrabold tracking-tight text-[#26272b]">
                  {orgName}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate font-heading text-[15px] font-bold leading-tight text-[#26272b]">
                  {orgName}
                </div>
                <div className="mt-0.5 text-xs leading-snug text-[#888]">{sentAtLabel}</div>
              </div>
            </div>
          </>
        )}

        <div className="px-8 py-8">
          {subject?.trim() && (
            <h1 className="mb-4 font-heading text-2xl font-extrabold leading-tight tracking-tight text-[#26272b]">
              {subject}
            </h1>
          )}
          {visible.length === 0 ? (
            <p className="text-sm text-[#888]">{emptyLabel}</p>
          ) : (
            <div className="flex flex-col">
              {visible.map((b, i) => {
                if (b.type === 'title') {
                  return (
                    <h2
                      key={i}
                      className="mb-4 font-heading text-[28px] font-extrabold leading-tight tracking-tight text-[#26272b]"
                    >
                      {b.text}
                    </h2>
                  );
                }
                if (b.type === 'subtitle') {
                  return (
                    <h3
                      key={i}
                      className="mb-3 font-heading text-xl font-bold leading-snug text-[#26272b]"
                    >
                      {b.text}
                    </h3>
                  );
                }
                if (b.type === 'body') {
                  return (
                    <div key={i} className="mb-4 flex flex-col gap-4 text-base leading-[1.6] text-[#26272b]">
                      {(b.text ?? '').split(/\n\n+/).map((para, pi) => (
                        <p key={pi}>{para}</p>
                      ))}
                    </div>
                  );
                }
                if (b.type === 'image') {
                  const src = blockImageSrc(b);
                  const pct = imageWidthPct(b);
                  return src ? (
                    <img
                      key={i}
                      src={src}
                      alt=""
                      className="mb-4 rounded-lg"
                      style={{
                        display: 'block',
                        width: `${pct}%`,
                        maxWidth: `${(544 * pct) / 100}px`,
                        marginInline: 'auto',
                      }}
                    />
                  ) : null;
                }
                return null;
              })}
            </div>
          )}
        </div>

        {includeFooter && (
          <div className="border-t border-[#EAEAEA] px-8 py-5 text-center text-xs leading-[1.6] text-[#888]">
            <p>
              {t('broadcast.editor.previewFooterAttribution', {
                org: orgName,
                defaultValue: `Sent by ${orgName} via SportAdmin`,
              })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function BlockEditor(props: {
  block: BroadcastBlock;
  disabled: boolean;
  onChange: (patch: Partial<BroadcastBlock>) => void;
  onRemove: () => void;
  onMoveUp: (() => void) | undefined;
  onMoveDown: (() => void) | undefined;
  upload: (file: File) => Promise<{ object_key: string }>;
}) {
  const { t } = useTranslation('common');
  const { block, disabled, onChange, onRemove, onMoveUp, onMoveDown, upload } = props;

  // Image blocks own their upload state + put crop/replace in the header,
  // so they render a dedicated editor instead of the text-input branches.
  if (block.type === 'image') {
    return (
      <ImageBlockEditor
        objectKey={block.object_key ?? ''}
        imageURL={block.image_url ?? ''}
        width={block.width}
        disabled={disabled}
        upload={upload}
        cropAspect={16 / 9}
        onChange={onChange}
        onRemove={onRemove}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
      />
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <BlockHeader
        type={block.type}
        disabled={disabled}
        onRemove={onRemove}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
      />
      {block.type === 'title' && (
        <Input
          type="text"
          value={block.text ?? ''}
          onChange={(e) => onChange({ text: e.target.value })}
          disabled={disabled}
          placeholder={t('broadcast.editor.titlePlaceholder', { defaultValue: 'Title' })}
          className="font-heading text-xl font-bold"
        />
      )}
      {block.type === 'subtitle' && (
        <Input
          type="text"
          value={block.text ?? ''}
          onChange={(e) => onChange({ text: e.target.value })}
          disabled={disabled}
          placeholder={t('broadcast.editor.subtitlePlaceholder', { defaultValue: 'Subtitle' })}
          className="font-heading text-base font-semibold"
        />
      )}
      {block.type === 'body' && (
        <textarea
          value={block.text ?? ''}
          onChange={(e) => onChange({ text: e.target.value })}
          disabled={disabled}
          rows={5}
          placeholder={t('broadcast.editor.bodyPlaceholder', { defaultValue: 'Body text' })}
          className="min-h-[120px] w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
      )}
    </div>
  );
}

// BlockHeader is the shared label + action row at the top of every block.
// `leadingActions` lets a block type inject extra controls (e.g. the image
// block's crop + replace buttons) to the left of the move/remove buttons,
// so every block control lives in one consistent row.
function BlockHeader({
  type,
  disabled,
  onRemove,
  onMoveUp,
  onMoveDown,
  leadingActions,
}: {
  type: BroadcastBlockType;
  disabled: boolean;
  onRemove: () => void;
  onMoveUp: (() => void) | undefined;
  onMoveDown: (() => void) | undefined;
  leadingActions?: React.ReactNode;
}) {
  const { t } = useTranslation('common');
  return (
    <div className="flex items-center gap-2">
      <BlockLabel type={type} />
      <div className="ml-auto flex items-center gap-1">
        {leadingActions}
        {onMoveUp && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onMoveUp}
            disabled={disabled}
            aria-label={t('broadcast.editor.moveUp', { defaultValue: 'Move up' })}
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
        )}
        {onMoveDown && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onMoveDown}
            disabled={disabled}
            aria-label={t('broadcast.editor.moveDown', { defaultValue: 'Move down' })}
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRemove}
          disabled={disabled}
          className="text-muted-foreground hover:text-destructive"
          aria-label={t('broadcast.editor.removeBlock', { defaultValue: 'Remove block' })}
        >
          <Trash className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function BlockLabel({ type }: { type: BroadcastBlockType }) {
  const { t } = useTranslation('common');
  const Icon =
    type === 'title' ? TextHOne : type === 'subtitle' ? TextHTwo : type === 'body' ? TextT : ImageIcon;
  return (
    <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      <Icon className="h-3.5 w-3.5" />
      {t(`broadcast.editor.block${type.charAt(0).toUpperCase() + type.slice(1)}`, {
        defaultValue: type,
      })}
    </span>
  );
}

// Accepted upload types mirror the broadcasts/upload-image handler's
// allowlist (internal/api/broadcasts.go:allowedBroadcastImageTypes).
const BROADCAST_IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';

// ImageBlockEditor is the image block's full editor — it renders the
// shared BlockHeader (move/remove) and injects the crop + replace (pen)
// buttons into that header's action row via `leadingActions`, so every
// control sits in one row and stays visible against the card rather than
// fighting a busy image. Deletion is the header trashcan; there is no
// second remove affordance on the image itself.
//
// Crop is gated on an uploaded object_key: the cropper draws to a canvas
// and a cross-origin stock image (image_url from a seed import) would
// taint it. Replace works in every state so a seeded stock photo can be
// swapped for the club's own image.
function ImageBlockEditor({
  objectKey,
  imageURL,
  width,
  disabled,
  upload,
  cropAspect,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  objectKey: string;
  imageURL: string;
  width?: number | undefined;
  disabled: boolean;
  upload: (file: File) => Promise<{ object_key: string }>;
  cropAspect?: number;
  onChange: (patch: Partial<BroadcastBlock>) => void;
  onRemove: () => void;
  onMoveUp: (() => void) | undefined;
  onMoveDown: (() => void) | undefined;
}) {
  const { t } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);

  // Uploaded object_key wins over a seeded image_url, matching the
  // backend Block.ImageSrc precedence.
  const src = objectKey ? `/storage/${objectKey}` : imageURL || '';
  const isSeeded = !objectKey && !!imageURL;
  const canCrop = !!objectKey && cropAspect !== undefined;
  // Clamp mirrors backend Block.ImageWidthPct (default 100 when unset).
  const widthPct = width && width > 0 ? Math.min(100, Math.max(10, width)) : 100;

  async function onFile(file: File) {
    setBusy(true);
    setErr(null);
    try {
      const result = await upload(file);
      onChange({ object_key: result.object_key, image_url: '' });
    } catch (e) {
      setErr(e instanceof ApiError ? e.code : 'upload_failed');
    } finally {
      setBusy(false);
    }
  }

  const onDrop = (ev: React.DragEvent) => {
    if (busy || disabled) return;
    ev.preventDefault();
    setDragOver(false);
    const file = Array.from(ev.dataTransfer.files).find((f) => f.type.startsWith('image/'));
    if (file) void onFile(file);
  };

  // Crop + replace live in the block header so they're always visible
  // against the card background rather than fighting the image. Only
  // shown once there's an image to act on.
  const headerActions =
    !disabled && src && !busy ? (
      <>
        {canCrop && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setCropOpen(true)}
            aria-label={t('imageInput.crop', { defaultValue: 'Crop' })}
          >
            <CropIcon className="h-4 w-4" />
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => inputRef.current?.click()}
          aria-label={t('imageInput.replace', { defaultValue: 'Replace' })}
        >
          <PencilSimple className="h-4 w-4" />
        </Button>
      </>
    ) : undefined;

  return (
    <div className="flex flex-col gap-2">
      <BlockHeader
        type="image"
        disabled={disabled}
        onRemove={onRemove}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        leadingActions={headerActions}
      />

      {src ? (
        <div className="relative overflow-hidden rounded-md border border-border">
          <img
            src={src}
            alt=""
            style={{ display: 'block', width: `${widthPct}%`, marginInline: 'auto' }}
          />
          {busy && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/70 text-xs font-medium text-foreground">
              {t('common.submitting', { defaultValue: 'Saving…' })}
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || busy}
          onDragOver={(e) => {
            if (busy || disabled) return;
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`flex h-40 w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 ${
            dragOver
              ? 'border-primary bg-primary/10 text-foreground'
              : 'border-border bg-muted/40 text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
        >
          <ImageIcon className="h-7 w-7" weight="duotone" aria-hidden />
          {busy
            ? t('common.submitting', { defaultValue: 'Saving…' })
            : dragOver
              ? t('imageInput.dropHere', { defaultValue: 'Drop to upload' })
              : t('imageInput.empty', { defaultValue: 'No image selected — drop a file or click choose' })}
        </button>
      )}

      {src && !disabled && (
        <label className="flex items-center gap-3 text-xs font-medium text-muted-foreground">
          <span className="whitespace-nowrap">
            {t('broadcast.editor.imageWidth', { defaultValue: 'Width' })}
          </span>
          <input
            type="range"
            min={10}
            max={100}
            step={5}
            value={widthPct}
            onChange={(e) => onChange({ width: Number(e.target.value) })}
            className="min-w-0 flex-1"
            style={{ accentColor: 'var(--lime)' }}
            aria-label={t('broadcast.editor.imageWidth', { defaultValue: 'Width' })}
          />
          <span className="w-10 text-right tabular-nums text-foreground">{widthPct}%</span>
        </label>
      )}

      {isSeeded && !disabled && (
        <p className="text-xs text-muted-foreground">
          {t('broadcast.editor.seededImageHint', {
            defaultValue: 'Suggested photo — replace it with your own using the pen.',
          })}
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={BROADCAST_IMAGE_ACCEPT}
        hidden
        disabled={disabled || busy}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void onFile(f);
          e.target.value = '';
        }}
      />

      {err && (
        <p className="text-xs text-destructive" role="alert">
          {t(`imageInput.error.${err}`, { defaultValue: t('imageInput.error.unknown') })}
        </p>
      )}

      {canCrop && (
        <ImageCropDialog
          open={cropOpen}
          src={`/storage/${objectKey}`}
          filename={objectKey}
          aspect={cropAspect}
          onCrop={async (file) => {
            const result = await upload(file);
            onChange({ object_key: result.object_key, image_url: '' });
          }}
          onClose={() => setCropOpen(false)}
        />
      )}
    </div>
  );
}
